import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Loader2, TrendingUp, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { UpgradeCard } from "@/components/ProGate";
import { fmtUsd, fmtPrice, fmtPct, CHAIN_META } from "@/lib/format";
import TokenReport from "@/components/TokenReport";

const CHAINS = ["all", "solana", "ethereum", "bsc", "base", "robinhood"];

const LAST_SCAN_KEY = "cc:lastScan";
const fmtAgoShort = (ts) => { const m = Math.round((Date.now() - ts) / 60000); return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`; };

export default function Scanner() {
  const location = useLocation();
  const [q, setQ] = useState("");
  const [chain, setChain] = useState("all");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [unsupported, setUnsupported] = useState(null);
  const [trending, setTrending] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [pendingScan, setPendingScan] = useState(null);
  const [providerBusy, setProviderBusy] = useState(null); // { retry: fn }
  const [restoredAt, setRestoredAt] = useState(null);
  const { plan, refreshPlan } = usePlan();

  useEffect(() => {
    let tries = 0;
    const fetchTrending = () => api.get("/trending").then(({ data }) => {
      setTrending(data.items);
      if (data.items.length === 0 && tries++ < 4) setTimeout(fetchTrending, 5000);
    }).catch(() => { setTrending([]); if (tries++ < 4) setTimeout(fetchTrending, 5000); });
    fetchTrending();
  }, []);

  const runSearch = useCallback(async (query, ch, attempt = 0) => {
    const term = (query ?? q).trim();
    if (!term) return;
    setSearching(true); setReport(null); setNotFound(false); setResults(null); setProviderBusy(null);
    try {
      const params = { q: term };
      const useChain = ch ?? chain;
      if (useChain && useChain !== "all") params.chain = useChain;
      const { data } = await api.get("/token/search", { params });
      setResults(data.results);
      setUnsupported(data.unsupported || null);
      setNotFound(data.results.length === 0);
      if (data.results.length === 1) loadReport(data.results[0].chain, data.results[0].address);
      else {
        const exact = data.results.find((r) => (r.address || "").toLowerCase() === q.trim().toLowerCase());
        if (exact) loadReport(exact.chain, exact.address);
      }
    } catch (err) {
      if (err.response?.status === 503) {
        // DexScreener rate-limited us: retry quietly twice before showing anything
        if (attempt < 2) { await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); return runSearch(term, ch, attempt + 1); }
        setProviderBusy({ retry: () => runSearch(term, ch, 0) });
      } else {
        toast.error("Search failed. Try again.");
      }
    } finally {
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, chain]);

  const loadReport = useCallback(async (ch, address, isRefresh = false, attempt = 0) => {
    if (isRefresh) setRefreshing(true); else setLoadingReport(true);
    setNotFound(false);
    setPaywall(null);
    setProviderBusy(null);
    try {
      const { data } = await api.get(`/token/${ch}/${address}`);
      setReport(data);
      setResults(null);
      setRestoredAt(null);
      try { localStorage.setItem(LAST_SCAN_KEY, JSON.stringify({ report: data, savedAt: Date.now() })); } catch {}
      refreshPlan();
    } catch (err) {
      if (err.response?.status === 402) {
        setPaywall(err.response.data?.detail || {});
        setPendingScan({ chain: ch, address });
        setResults(null);
        setReport(null);
      } else if (err.response?.status === 503) {
        if (attempt < 2) { await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); return loadReport(ch, address, isRefresh, attempt + 1); }
        if (isRefresh) toast.error("Market data is busy — showing the previous snapshot");
        else setProviderBusy({ retry: () => loadReport(ch, address, false, 0) });
      } else if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setNotFound(true);
        toast.error("Could not load token report");
      }
    } finally {
      setLoadingReport(false); setRefreshing(false);
    }
  }, []);

  // Auto-load from watchlist navigation, otherwise restore the last scan so it survives tab changes
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const pf = location.state?.scan || (qs.get("chain") && qs.get("address") ? { chain: qs.get("chain"), address: qs.get("address") } : null);
    if (pf?.chain && pf?.address) {
      setQ(pf.symbol || "");
      loadReport(pf.chain, pf.address);
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(LAST_SCAN_KEY) || "null");
      if (saved?.report?.token && !report) { setReport(saved.report); setRestoredAt(saved.savedAt); }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search]);

  const clearReport = () => { setReport(null); setResults(null); setQ(""); setRestoredAt(null); try { localStorage.removeItem(LAST_SCAN_KEY); } catch {} };

  // Hit the paywall as a guest, then signed in / went Pro → retry the same scan automatically
  useEffect(() => {
    if (paywall && plan?.pro && pendingScan) {
      setPaywall(null);
      loadReport(pendingScan.chain, pendingScan.address);
    }
  }, [plan?.pro, paywall, pendingScan, loadReport]);

  const submit = (e) => { e.preventDefault(); runSearch(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 sm:py-12">
      {/* Hero */}
      {!report && (
        <div className="text-center max-w-3xl mx-auto mb-5 sm:mb-8 fade-up">
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 mb-3 sm:mb-5 font-mono text-[10px] sm:text-[11px] uppercase tracking-widest">
            Solana · Ethereum · BNB · Base
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            Scan before you <span className="text-emerald-400 text-glow-green">ape.</span>
          </h1>
          <p className="text-slate-400 mt-3 sm:mt-4 text-sm sm:text-lg">
            Paste any memecoin ticker or contract address. Get instant rug risk, bundle detection, holder breakdown and a plain-English AI verdict.
          </p>
        </div>
      )}

      {/* Search */}
      <form onSubmit={submit} className="max-w-3xl mx-auto mb-5 sm:mb-6">
        <div className="flex flex-row gap-2 sm:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="token-search-input"
              placeholder="$BONK, PEPE, or contract…"
              className="h-12 sm:h-14 pl-12 pr-12 text-base bg-[#111723] border-[#334566] focus-visible:ring-emerald-500 rounded-xl" />
            {q && (
              <button type="button" onClick={() => { setQ(""); setResults(null); setNotFound(false); }}
                data-testid="clear-search-btn" aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-[#222F43] transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button type="submit" disabled={searching} data-testid="search-btn"
            className="tap h-12 sm:h-14 px-5 sm:px-8 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-base rounded-xl">
            {searching ? <Loader2 className="h-5 w-5 animate-spin" /> : "Scan"}
          </Button>
        </div>
        <div className="flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar items-center gap-2 mt-3 -mx-4 px-4 sm:mx-0 sm:px-0">
          {CHAINS.map((c) => (
            <button key={c} type="button" onClick={() => setChain(c)} data-testid={`chain-${c}`}
              className={`tap shrink-0 px-3 py-1.5 sm:py-1 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${
                chain === c ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" : "text-slate-400 border border-[#222F43] hover:border-[#334566]"
              }`}>
              {c === "all" ? "All chains" : CHAIN_META[c]?.label || c}
            </button>
          ))}
        </div>
      </form>

      {plan && !plan.pro && !report && !paywall && (
        <p className="text-center text-[11px] font-mono text-slate-500 -mt-3 mb-4" data-testid="scans-left">
          {plan.scansLeft > 0 ? `${plan.scansLeft} free scan left today` : "Free scan used for today"} · <Link to="/pricing" className="text-emerald-400 hover:underline">Go Pro for unlimited</Link>
        </p>
      )}

      {/* Trending — live, highest on-chain volume across chains */}
      {!report && !results && !loadingReport && (
        <div className="max-w-3xl mx-auto mb-4" data-testid="trending-row">
          <div className="flex items-center gap-1 text-xs text-slate-500 font-mono mb-2 sm:hidden"><TrendingUp className="h-3.5 w-3.5" /> Trending this week</div>
          <div className="flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar sm:justify-center items-center gap-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            <span className="hidden sm:flex text-xs text-slate-500 font-mono items-center gap-1" title="Memecoins with the highest DEX volume this week — pump.fun, bonk.fun, four.meme & co."><TrendingUp className="h-3.5 w-3.5" /> Trending this week:</span>
            {trending === null && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-6 w-16 shrink-0 rounded-full bg-[#111723]" />)}
            {trending && trending.length === 0 && <span className="text-xs text-slate-600">warming up…</span>}
            {trending && trending.map((t) => (
              <button key={`${t.chain}-${t.address}`} onClick={() => { setQ(t.symbol); loadReport(t.chain, t.address); }} data-testid={`trending-${t.symbol}`}
                title={`${t.launchpad ? `${t.launchpad} · ` : ""}${CHAIN_META[t.chain]?.label || t.chain} · ${fmtUsd(t.volume7d)} vol / ${t.days}d`}
                className="tap shrink-0 flex items-center gap-1.5 px-3 py-1.5 sm:py-1 rounded-full text-xs font-mono bg-[#111723] border border-[#222F43] text-slate-300 hover:border-emerald-500/40 hover:text-emerald-400 transition-colors">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: CHAIN_META[t.chain]?.color || "#94A3B8" }} />
                ${t.symbol}
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingReport && (
        <div className="space-y-4 max-w-5xl mx-auto">
          <Skeleton className="h-40 w-full bg-[#111723]" />
          <Skeleton className="h-24 w-full bg-[#111723]" />
          <div className="grid lg:grid-cols-2 gap-4">
            <Skeleton className="h-64 bg-[#111723]" /><Skeleton className="h-64 bg-[#111723]" />
          </div>
        </div>
      )}

      {providerBusy && !loadingReport && !searching && (
        <Card className="max-w-2xl mx-auto bg-[#111723] border-amber-500/30 p-8 text-center" data-testid="provider-busy">
          <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-3" />
          <p className="text-slate-200 font-semibold">Market data is busy right now</p>
          <p className="text-slate-400 text-sm mt-1">Our price provider rate-limited the request. This isn't about your coin — it usually clears in a few seconds.</p>
          <Button onClick={providerBusy.retry} className="tap mt-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="provider-retry-btn">Try again</Button>
        </Card>
      )}

      {notFound && !loadingReport && !providerBusy && !(results && results.length > 0) && (
        <Card className="max-w-2xl mx-auto bg-[#111723] border-[#222F43] p-8 text-center" data-testid="not-found">
          <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-3" />
          {unsupported ? (
            <div data-testid="unsupported-chain">
              <p className="text-slate-200 font-semibold">Found <span className="text-emerald-400">${unsupported.symbol}</span> — but it's on <span className="capitalize">{unsupported.chain.replace(/-/g, " ")}</span>, a chain we don't support yet.</p>
              <p className="text-slate-400 text-sm mt-2">CatalystCaddy currently scans Solana, Ethereum, BNB Chain, Base and Robinhood Chain.</p>
              {unsupported.url && <a href={unsupported.url} target="_blank" rel="noreferrer" className="inline-block mt-3 text-xs font-mono text-slate-400 hover:text-emerald-400 underline-offset-2 hover:underline">View on DexScreener ↗</a>}
            </div>
          ) : (
            <p className="text-slate-300">No token found on the supported DEXes. Check the ticker or paste the full contract address.</p>
          )}
        </Card>
      )}

      {paywall && (
        <div className="max-w-3xl mx-auto mb-8" data-testid="scan-paywall">
          <UpgradeCard testId="scan-paywall-card"
            title={paywall.reason === "daily_limit" ? "You've used today's free scan" : "Scanning is a Pro feature"}
            subtitle={paywall.guest
              ? "Guests get 1 free scan a day. Sign up for Pro to scan every coin you're eyeing — and see the Catalyst Calendar of what's about to move the market."
              : "Free accounts get 1 scan a day. Pro unlocks unlimited scans, AI takes, the full Catalyst Calendar and your trade journal."} />
        </div>
      )}

      {/* Candidate results */}
      {results && results.length > 0 && (
        <div className="max-w-4xl mx-auto space-y-2" data-testid="search-results">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">{results.length} matches — pick one to scan</p>
          {results.map((r) => {
            const cm = CHAIN_META[r.chain] || { label: r.chain, color: "#94A3B8" };
            const up = (r.priceChange?.h24 || 0) >= 0;
            return (
              <button key={`${r.chain}-${r.address}`} onClick={() => loadReport(r.chain, r.address)}
                data-testid={`result-${r.symbol}`}
                className="w-full text-left flex items-center gap-3 p-4 rounded-xl bg-[#111723] border border-[#222F43] hover:border-emerald-500/40 hover:bg-[#172030] transition-all">
                {r.imageUrl ? <img src={r.imageUrl} alt="" className="h-10 w-10 rounded-full" /> :
                  <div className="h-10 w-10 rounded-full bg-[#172030] flex items-center justify-center font-bold text-emerald-400 text-sm">{r.symbol?.slice(0, 2)}</div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">${r.symbol}</span>
                    <Badge variant="outline" style={{ borderColor: cm.color + "66", color: cm.color }} className="bg-transparent text-[9px] uppercase">{cm.label}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{r.name} · {r.dexId}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm">{fmtPrice(r.priceUsd)}</div>
                  <div className={`text-xs font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>{fmtPct(r.priceChange?.h24)}</div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <div className="font-mono text-sm">{fmtUsd(r.liquidityUsd)}</div>
                  <div className="text-[10px] text-slate-500 uppercase">liq</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {report && !loadingReport && (
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-3 sm:mb-4 gap-3">
            <button onClick={clearReport} className="tap text-xs text-slate-400 hover:text-emerald-400 font-mono py-1" data-testid="back-to-search">
              ← new scan
            </button>
            {restoredAt && (
              <span className="text-[11px] font-mono text-slate-500" data-testid="restored-note">
                Last scan · {fmtAgoShort(restoredAt)} — <button onClick={() => loadReport(report.token.chain, report.token.address, true)} className="text-emerald-400 hover:underline" data-testid="restored-refresh">refresh for new data</button>
              </span>
            )}
          </div>
          <TokenReport report={report} refreshing={refreshing} onRefresh={() => loadReport(report.token.chain, report.token.address, true)} />
        </div>
      )}
    </div>
  );
}
